import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button, Spinner, Alert, Table } from '../../shim/reactstrap';
import Select from 'react-select';
import apiClient from '../../api/ApiClient';

// Default cap for the per-extra date lists rendered inline. The backend
// returns every date (no truncation) but rendering thousands of rows
// stalls the page, so we collapse the rest into a "(… N more)" footnote
// and offer an expander. The cap is intentionally generous — most
// real-world diffs are in the single digits.
const DEFAULT_INLINE_DATE_LIMIT = 50;

// Labels one PackageTicker row for use in a react-select dropdown. The
// shape is: "DATA / AAPL (Polygon) — Apple Inc.   [#42]". The Subfolder
// is shown first because the user is choosing "which CSV in which
// folder" — the Code/Name pair only matters second. The trailing row id
// is a tie-breaker for two rows that share Code/Name (e.g. the same
// ticker mapped to two subfolders).
const formatPackageTickerLabel = (pt) => {
  if (!pt) return '';
  const subfolder = (pt.Subfolder && pt.Subfolder.trim()) || 'DATA';
  const source = pt.Ticker?.Source ? ` (${pt.Ticker.Source})` : '';
  const name = pt.Name ? ` — ${pt.Name}` : '';
  return `${subfolder} / ${pt.Code}${source}${name}   [#${pt.Id}]`;
};

// Converts a PackageTickerResponse from the backend into a react-select
// option. We keep the full row on the option so onChange callbacks can
// render the result without a second lookup.
const toOption = (pt) => ({
  value: pt.Id,
  label: formatPackageTickerLabel(pt),
  raw: pt,
});

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
};

// Renders a sorted list of dates inline, collapsing the tail into a
// "(… N more)" line once the cap is hit. The full list is always
// available behind the expander so the admin does not have to leave the
// page to inspect every missing/extra date.
const DateList = ({ dates, cap = DEFAULT_INLINE_DATE_LIMIT }) => {
  const [expanded, setExpanded] = useState(false);

  if (!dates || dates.length === 0) {
    return <span className="text-muted small">No dates</span>;
  }

  const visible = expanded ? dates : dates.slice(0, cap);
  const remaining = dates.length - visible.length;

  return (
    <div>
      <div style={{ maxHeight: expanded ? 'none' : '200px', overflowY: 'auto' }}>
        {visible.map((d, idx) => (
          <div key={`${d}-${idx}`} style={{ fontSize: '0.85em' }}>
            {formatDate(d)}
          </div>
        ))}
      </div>
      {remaining > 0 && (
        <Button
          color="link"
          size="sm"
          className="p-0 mt-1"
          onClick={() => setExpanded(true)}
          style={{ textDecoration: 'underline' }}
        >
          … {remaining} more
        </Button>
      )}
      {expanded && dates.length > cap && (
        <Button
          color="link"
          size="sm"
          className="p-0 mt-1"
          onClick={() => setExpanded(false)}
          style={{ textDecoration: 'underline' }}
        >
          Collapse
        </Button>
      )}
    </div>
  );
};

// Renders one extra PackageTicker's diff result. Pulled out of the main
// render so the parent can stay focused on the picker UX.
const ExtraResultPanel = ({ result }) => {
  const missingCount = result.MissingDates?.length || 0;
  const extraCount = result.ExtraDates?.length || 0;
  const bothEmpty = missingCount === 0 && extraCount === 0;
  let banner;
  if (result.PrimaryFileMissing) {
    banner = (
      <Alert color="danger" fade={false} className="mb-2">
        Primary CSV not found in the Analysis folder ({result.Primary?.CsvPath || 'no path'}).
        The diff is empty — re-generate the Analysis package first.
      </Alert>
    );
  } else if (result.ExtraFileMissing) {
    banner = (
      <Alert color="warning" fade={false} className="mb-2">
        Extra CSV not found in the Analysis folder
        {result.Extra?.CsvPath ? ` (${result.Extra.CsvPath})` : ''}.
        The diff is empty until the Analysis package contains this CSV.
      </Alert>
    );
  }

  return (
    <div className="mb-4 pb-3 border-bottom">
      <div className="d-flex flex-wrap align-items-baseline mb-2">
        <h5 className="mb-0 me-3">
          Extra:&nbsp;{result.Extra?.Subfolder || '?'} / {result.Extra?.Code || '?'}
          {result.Extra?.Source ? (
            <span className="badge badge-info ms-2" style={{ fontSize: '0.6em', verticalAlign: 'middle' }}>
              {result.Extra.Source}
            </span>
          ) : null}
        </h5>
        <span className="text-muted small">PackageTicker #{result.Extra?.Id}</span>
      </div>
      <div className="text-muted small mb-2">
        CSV:&nbsp;
        {result.Extra?.CsvPath ? (
          <span style={{ fontFamily: 'monospace' }}>{result.Extra.CsvPath}</span>
        ) : (
          <span className="text-danger">not found</span>
        )}
      </div>
      <Table bordered size="sm" className="mb-2">
        <tbody>
          <tr>
            <th style={{ width: '180px' }}>Primary date range</th>
            <td>
              {formatDate(result.PrimaryFirstDate)} – {formatDate(result.PrimaryLastDate)}
              &nbsp;<span className="text-muted">({result.PrimaryDateCount} dates)</span>
            </td>
          </tr>
          <tr>
            <th>Extra date range</th>
            <td>
              {formatDate(result.ExtraFirstDate)} – {formatDate(result.ExtraLastDate)}
              &nbsp;<span className="text-muted">({result.ExtraDateCount} dates)</span>
            </td>
          </tr>
          {result.IntersectionFirstDate && result.IntersectionLastDate ? (
            <tr>
              <th>Overlap (diff range)</th>
              <td>
                {formatDate(result.IntersectionFirstDate)} – {formatDate(result.IntersectionLastDate)}
              </td>
            </tr>
          ) : null}
          <tr>
            <th>Missing in extra</th>
            <td>
              <span className={missingCount > 0 ? 'text-danger fw-semibold' : 'text-success'}>
                {missingCount}
              </span>
              {missingCount > 0 && (
                <span className="text-muted">&nbsp;date(s) present in primary but absent in extra (within overlap)</span>
              )}
            </td>
          </tr>
          <tr>
            <th>Extra in extra</th>
            <td>
              <span className={extraCount > 0 ? 'text-danger fw-semibold' : 'text-success'}>
                {extraCount}
              </span>
              {extraCount > 0 && (
                <span className="text-muted">&nbsp;date(s) present in extra but absent in primary (within overlap)</span>
              )}
            </td>
          </tr>
        </tbody>
      </Table>
      {banner}
      {(missingCount > 0 || extraCount > 0) && (
        <div className="row">
          <div className="col-md-6">
            <div className="mb-1">
              <strong>Missing dates (in primary, not in extra, within overlap):</strong>{' '}
              <span className="text-muted small">{missingCount}</span>
            </div>
            <DateList dates={result.MissingDates || []} />
          </div>
          <div className="col-md-6">
            <div className="mb-1">
              <strong>Extra dates (in extra, not in primary, within overlap):</strong>{' '}
              <span className="text-muted small">{extraCount}</span>
            </div>
            <DateList dates={result.ExtraDates || []} />
          </div>
        </div>
      )}
      {bothEmpty && !banner && (
        <Alert color="success" fade={false} className="mb-0">
          No date-set difference within the overlapping range.
        </Alert>
      )}
    </div>
  );
};

export const AdminPackageTickersDatesValidation = () => {
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [packageTickers, setPackageTickers] = useState([]);

  const [primaryOption, setPrimaryOption] = useState(null);
  const [extraOptions, setExtraOptions] = useState([]);

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState(null);
  const [report, setReport] = useState(null);

  // Load the full PackageTickers list once on mount. The list is small
  // (one row per admin-managed ticker mapping) so loading it all and
  // filtering client-side is cheaper than round-tripping per keystroke.
  const loadPackageTickers = useCallback(async () => {
    try {
      setLoadingList(true);
      setLoadError(null);
      const rows = await apiClient.listPackageTickers();
      // Stable order so the dropdown is predictable: sort by Subfolder
      // (case-insensitive), then by Code, then by Id.
      const sorted = (rows || []).slice().sort((a, b) => {
        const sf = String(a.Subfolder || 'DATA').localeCompare(String(b.Subfolder || 'DATA'), undefined, { sensitivity: 'base' });
        if (sf !== 0) return sf;
        const cf = String(a.Code || '').localeCompare(String(b.Code || ''), undefined, { sensitivity: 'base' });
        if (cf !== 0) return cf;
        return (a.Id || 0) - (b.Id || 0);
      });
      setPackageTickers(sorted);
    } catch (exc) {
      console.error('Failed to load package tickers', exc);
      setLoadError(exc.message || 'Failed to load package tickers');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadPackageTickers();
  }, [loadPackageTickers]);

  // Pre-built react-select option list. Memoised so changing the
  // primary dropdown does not force react-select to re-render every
  // option's label.
  const allOptions = useMemo(
    () => packageTickers.map(toOption),
    [packageTickers],
  );

  // Whenever the primary changes, drop the matching id from the extras
  // so the user can never submit a self-comparison. This is a UX safety
  // net on top of the backend's defensive filter.
  useEffect(() => {
    if (!primaryOption) {
      if (extraOptions.length > 0) setExtraOptions([]);
      return;
    }
    const filtered = extraOptions.filter((o) => o.value !== primaryOption.value);
    if (filtered.length !== extraOptions.length) {
      setExtraOptions(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryOption]);

  const handleAnalyze = async () => {
    setAnalyzeError(null);
    setReport(null);

    if (!primaryOption) {
      setAnalyzeError('Pick a primary PackageTicker first.');
      return;
    }
    if (!extraOptions || extraOptions.length === 0) {
      setAnalyzeError('Pick at least one extra PackageTicker to compare against.');
      return;
    }

    const body = {
      PrimaryPackageTickerId: primaryOption.value,
      ExtraPackageTickerIds: extraOptions.map((o) => o.value),
    };

    try {
      setAnalyzing(true);
      const result = await apiClient.analyzePackageTickersDatesValidation(body);
      setReport(result);
    } catch (exc) {
      console.error('Failed to run dates validation', exc);
      const msg = exc?.structuredErrors
        ? exc.structuredErrors.map((e) => e.Message).join('; ')
        : (exc.message || 'Failed to run dates validation');
      setAnalyzeError(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  // Pre-flight validation messages surfaced below the Analyze button so
  // the user does not have to click the button to discover an empty
  // picker.
  const primaryMissing = !primaryOption;
  const extrasMissing = !extraOptions || extraOptions.length === 0;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Package Tickers Dates Validation</h2>
      </div>

      <p className="text-muted">
        Pick a primary <strong>PackageTicker</strong> and one or more extras. Each
        extra is compared against the primary by reading the corresponding
        CSV from the Analysis folder (the same one the{' '}
        <a href="/admin/models-analysis">Models Analysis</a> page uses). For
        every extra the report shows the dates present in the primary but
        missing from the extra, and the dates present in the extra but
        missing from the primary.
      </p>

      {loadError && (
        <Alert color="danger" fade={false}>
          {loadError}
        </Alert>
      )}

      <div className="parameters-pane mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label fw-semibold">Primary PackageTicker</label>
            <Select
              options={allOptions}
              value={primaryOption}
              onChange={setPrimaryOption}
              placeholder={loadingList ? 'Loading…' : 'Pick the primary data source…'}
              isClearable
              isDisabled={loadingList}
            />
            <div className="form-text">
              The data source whose date coverage you want to use as the
              baseline.
            </div>
          </div>
          <div className="col-md-6">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <label className="form-label fw-semibold mb-0">Extra PackageTickers</label>
              {primaryOption ? (
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    const others = allOptions.filter((o) => o.value !== primaryOption.value);
                    setExtraOptions(others);
                  }}
                  style={{ textDecoration: 'underline' }}
                >
                  Add all
                </Button>
              ) : null}
            </div>
            <Select
              isMulti
              options={allOptions}
              value={extraOptions}
              onChange={setExtraOptions}
              placeholder={loadingList ? 'Loading…' : 'Pick one or more extras…'}
              isDisabled={loadingList}
            />
            <div className="form-text">
              Each extra is compared independently. Picking extras that
              share the primary's <code>Code</code> (e.g. AAPL across Polygon
              and Alpaca) is the most common use case.
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <Button
          color="primary"
          onClick={handleAnalyze}
          disabled={analyzing || primaryMissing || extrasMissing}
        >
          {analyzing ? (
            <>
              <Spinner size="sm" className="me-2" />
              Analyzing…
            </>
          ) : (
            'Analyze'
          )}
        </Button>
        {(primaryMissing || extrasMissing) && (
          <span className="ms-3 text-muted small">
            Pick a primary{primaryMissing && extrasMissing ? ' and at least one extra' : extrasMissing ? ' plus at least one extra' : ''} to enable Analyze.
          </span>
        )}
      </div>

      {analyzeError && (
        <Alert color="danger" fade={false}>
          {analyzeError}
        </Alert>
      )}

      {report && (
        <>
          <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
            <div className="d-flex flex-wrap align-items-center">
              <strong className="me-3">Analysis package:</strong>
              {report.AnalysisPackageGeneratedAt ? (
                <span className="text-success me-3">
                  Generated at{' '}
                  {new Date(report.AnalysisPackageGeneratedAt).toLocaleString()}
                  {report.AnalysisPackageFolder && (
                    <span
                      className="ms-2 text-muted"
                      style={{ fontSize: '0.85em', fontFamily: 'monospace' }}
                    >
                      {report.AnalysisPackageFolder}
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-warning me-3">
                  Not generated yet — comparisons will run against whatever
                  CSVs are already on disk.
                </span>
              )}
            </div>
            <div className="text-muted mt-2" style={{ fontSize: '0.85em' }}>
              The Analysis package is a CSV mirror of the current CTRAC package under{' '}
              <code>LocalStorage.Path/Analysis/</code>.
            </div>
          </div>

          <div className="mb-4">
            <h3>Primary</h3>
            <div className="ms-3">
              <div>
                <strong>Subfolder / Code:</strong>{' '}
                <code>{report.Primary?.Subfolder || '?'}</code> /{' '}
                <code>{report.Primary?.Code || '?'}</code>
                {report.Primary?.Source ? (
                  <span className="badge badge-info ms-2" style={{ fontSize: '0.7em', verticalAlign: 'middle' }}>
                    {report.Primary.Source}
                  </span>
                ) : null}
              </div>
              <div>
                <strong>Name:</strong> {report.Primary?.Name || '—'}
              </div>
              <div className="text-muted small">
                PackageTicker #{report.Primary?.Id} (underlying ticker #
                {report.Primary?.TickerId})
              </div>
              <div className="text-muted small" style={{ fontFamily: 'monospace' }}>
                {report.Primary?.CsvPath || 'CSV not found'}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h3>Comparison</h3>
            {(!report.ExtraResults || report.ExtraResults.length === 0) && (
              <Alert color="info" fade={false}>
                No comparison results.
              </Alert>
            )}
            {report.ExtraResults?.map((r, idx) => (
              <ExtraResultPanel
                // The list is built per-analyze in a stable order, so the
                // index is a safe React key.
                // eslint-disable-next-line react/no-array-index-key
                key={idx}
                result={r}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
